import {
  ComplianceDocument_Create,
  ComplianceDocument_Update,
  deleteV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsId,
  deleteV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsId,
  deleteV1CompaniesCompanyIdUsersUserIdComplianceDocumentsId,
  getV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsChecklist,
  getV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsChecklist,
  getV1CompaniesCompanyIdUsersUserIdComplianceDocumentsChecklist,
  patchV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsId,
  patchV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsId,
  patchV1CompaniesCompanyIdUsersUserIdComplianceDocumentsId,
  postV1CompaniesCompanyIdAccountsAccountIdComplianceDocuments,
  postV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocuments,
  postV1CompaniesCompanyIdUsersUserIdComplianceDocuments,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ChecklistItem, parseChecklistItem } from './checklist';
import { ComplianceDocument, CompliantType } from './models';

export default function useComplianceDocuments(companyIdOverride?: string) {
  const { userStore } = useStores();
  const companyId = companyIdOverride || getEffectiveUserCompanyId(userStore);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchChecklist = async (compliantType: CompliantType, compliantId: string) => {
    setIsLoading(true);

    try {
      let response;

      switch (compliantType) {
        case 'Account':
          response =
            await getV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsChecklist({
              path: { 'company-id': companyId, 'account-id': compliantId },
            });
          break;
        case 'User':
          response = await getV1CompaniesCompanyIdUsersUserIdComplianceDocumentsChecklist(
            {
              path: { 'company-id': companyId, 'user-id': compliantId },
            },
          );
          break;
        case 'Equipment':
          response =
            await getV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsChecklist(
              {
                path: { 'company-id': companyId, 'equipment-id': compliantId },
              },
            );
          break;
        default:
          throw new Error(`Unsupported compliant type: ${compliantType}`);
      }
      const items = (response.data.data ?? []).map(parseChecklistItem);

      setChecklist(items);

      return items;
    } catch (error) {
      setChecklist([]);
      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_fetch_checklist'),
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChecklistRef = useRef(fetchChecklist);
  fetchChecklistRef.current = fetchChecklist;

  const getChecklist = useCallback(
    (compliantType: CompliantType, compliantId: string) => {
      return fetchChecklistRef.current(compliantType, compliantId);
    },
    [],
  );

  const createDocument = async (
    compliantType: CompliantType,
    compliantId: string,
    body: Record<string, unknown>,
  ) => {
    try {
      const typedBody = body as ComplianceDocument_Create;
      let response;

      switch (compliantType) {
        case 'Account':
          response = await postV1CompaniesCompanyIdAccountsAccountIdComplianceDocuments({
            path: { 'company-id': companyId, 'account-id': compliantId },
            body: typedBody,
          });
          break;
        case 'User':
          response = await postV1CompaniesCompanyIdUsersUserIdComplianceDocuments({
            path: { 'company-id': companyId, 'user-id': compliantId },
            body: typedBody,
          });
          break;
        case 'Equipment':
          response =
            await postV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocuments({
              path: { 'company-id': companyId, 'equipment-id': compliantId },
              body: typedBody,
            });
          break;
        default:
          throw new Error(`Unsupported compliant type: ${compliantType}`);
      }

      return ComplianceDocument.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_save'),
      );
      throw error;
    }
  };

  const updateDocument = async (
    compliantType: CompliantType,
    compliantId: string,
    documentId: string,
    body: Record<string, unknown>,
  ) => {
    try {
      const typedBody = body as ComplianceDocument_Update;
      let response;

      switch (compliantType) {
        case 'Account':
          response =
            await patchV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsId({
              path: {
                'company-id': companyId,
                'account-id': compliantId,
                id: documentId,
              },
              body: typedBody,
            });
          break;
        case 'User':
          response = await patchV1CompaniesCompanyIdUsersUserIdComplianceDocumentsId({
            path: { 'company-id': companyId, 'user-id': compliantId, id: documentId },
            body: typedBody,
          });
          break;
        case 'Equipment':
          response =
            await patchV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsId({
              path: {
                'company-id': companyId,
                'equipment-id': compliantId,
                id: documentId,
              },
              body: typedBody,
            });
          break;
        default:
          throw new Error(`Unsupported compliant type: ${compliantType}`);
      }
      return ComplianceDocument.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_save'),
      );
      throw error;
    }
  };

  const deleteDocument = async (
    compliantType: CompliantType,
    compliantId: string,
    documentId: string,
  ) => {
    try {
      switch (compliantType) {
        case 'Account':
          await deleteV1CompaniesCompanyIdAccountsAccountIdComplianceDocumentsId({
            path: { 'company-id': companyId, 'account-id': compliantId, id: documentId },
          });
          break;
        case 'User':
          await deleteV1CompaniesCompanyIdUsersUserIdComplianceDocumentsId({
            path: { 'company-id': companyId, 'user-id': compliantId, id: documentId },
          });
          break;
        case 'Equipment':
          await deleteV1CompaniesCompanyIdEquipmentEquipmentIdComplianceDocumentsId({
            path: {
              'company-id': companyId,
              'equipment-id': compliantId,
              id: documentId,
            },
          });
          break;
        default:
          throw new Error(`Unsupported compliant type: ${compliantType}`);
      }
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_delete'),
      );
      throw error;
    }
  };

  return {
    checklist,
    createDocument,
    deleteDocument,
    getChecklist,
    isLoading,
    updateDocument,
  };
}
